<template>
    <div class="secret-wrap">
      <div class="container">
        <!-- 顶部快捷栏 -->
        <div class="quick-bar">
          <div class="bar-title">API密钥管理</div>
          <div class="btn-group">
            <el-button type="primary" @click="openCreateDialog">创建密钥</el-button>
            <el-button @click="$router.push('/account')">返回账户信息</el-button>
          </div>
        </div>
  
        <!-- 安全提示卡片 -->
        <div class="warn-card">
          <span class="warn-dot"></span>
          <span class="warn-text">
            重要安全提醒：SecretKey仅创建时可见，请妥善保存，请勿泄露、不要明文放置在前端页面。
            如密钥存在泄露风险，请立即禁用并删除，重新生成新密钥。
          </span>
        </div>
  
        <!-- 查询筛选区域 -->
        <div class="filter-card">
          <div class="search-form">
            <el-form :model="query" inline>
              <el-form-item label="密钥名称">
                <el-input
                  v-model="query.keyword"
                  placeholder="密钥备注名称"
                  clearable
                  @keyup.enter="handleSearch"
                />
              </el-form-item>
              <el-form-item label="状态">
                <el-select v-model="query.status" clearable placeholder="全部" style="width:110px">
                  <el-option label="正常启用" value="enable" />
                  <el-option label="已禁用" value="disable" />
                </el-select>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" @click="handleSearch">查询</el-button>
                <el-button @click="resetQuery">重置</el-button>
              </el-form-item>
            </el-form>
          </div>
        </div>
  
        <!-- 密钥表格 -->
        <div class="table-wrap">
          <el-table
            :data="tableList"
            border
            stripe
            class="secret-table"
            ref="tableRef"
          >
            <el-table-column label="密钥名称" prop="secretName" align="center" min-width="140" />
            <el-table-column label="AccessKey" align="center" min-width="220">
              <template #default="scope">
                <div class="ak-row">
                  <span class="ak-text">{{ scope.row.accessKey }}</span>
                  <el-button text type="primary" size="small" @click="copyText(scope.row.accessKey)">复制</el-button>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="状态" align="center" width="110">
              <template #default="scope">
                <el-tag :type="scope.row.status === 'enable' ? 'success' : 'danger'">
                  {{ scope.row.status === 'enable' ? '启用' : '禁用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="创建时间" prop="createTime" align="center" width="180" />
            <el-table-column label="最近调用时间" prop="lastCallTime" align="center" width="180">
              <template #default="scope">
                {{ scope.row.lastCallTime || '-' }}
              </template>
            </el-table-column>
            <el-table-column label="操作" align="center" width="220">
              <template #default="scope">
                <el-button
                  text
                  type="warning"
                  @click="toggleStatus(scope.row)"
                >
                  {{ scope.row.status === 'enable' ? '禁用' : '启用' }}
                </el-button>
                <el-button text type="danger" @click="deleteSecret(scope.row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
  
        <!-- 分页 -->
        <div class="page-bar">
          <el-pagination
            v-model:current-page="page.pageNum"
            v-model:page-size="page.pageSize"
            :total="page.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSearch"
            @current-change="handleSearch"
          />
        </div>
      </div>
  
      <!-- 创建密钥弹窗 -->
      <el-dialog v-model="createDialogVisible" title="创建API密钥" width="460px">
        <el-form :model="createForm" label-width="100px">
          <el-form-item label="密钥备注名称">
            <el-input v-model="createForm.secretName" placeholder="例如：生产环境服务调用" />
            <div style="font-size:12px;color:#9ca3af;margin-top:4px">用于区分不同业务使用场景</div>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="createDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="createSecret">确认创建</el-button>
        </template>
      </el-dialog>
  
      <!-- 创建成功，展示Secret（一次性弹窗） -->
      <el-dialog v-model="secretShowDialog" title="密钥创建成功！请勿丢失" width="520px" :close-on-click-modal="false">
        <div class="secret-show-box">
          <div class="show-row">
            <label>AccessKey：</label>
            <div class="copy-line">
              <span>{{ newSecret.accessKey }}</span>
              <el-button text @click="copyText(newSecret.accessKey)">复制</el-button>
            </div>
          </div>
          <div class="show-row">
            <label>SecretKey：</label>
            <div class="copy-line">
              <span>{{ newSecret.secretKey }}</span>
              <el-button text @click="copyText(newSecret.secretKey)">复制</el-button>
            </div>
          </div>
        </div>
        <div class="danger-tip">
          ⚠️ 关闭弹窗后SecretKey将无法再次查看，请立即复制保存！
        </div>
        <template #footer>
          <el-button type="primary" @click="secretShowDialog = false">我已保存，关闭</el-button>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script setup>
  import { ref, reactive, onMounted } from 'vue'
  import { ElMessage, ElMessageBox } from 'element-plus'
  // import api from '@/api/api'
  
  const tableRef = ref(null)
  
  // 查询条件
  const query = reactive({
    keyword: '',
    status: ''
  })
  
  // 分页
  const page = reactive({
    pageNum: 1,
    pageSize: 10,
    total: 0
  })
  
  // 表格数据
  const tableList = ref([])
  
  // 创建密钥弹窗
  const createDialogVisible = ref(false)
  const createForm = reactive({
    secretName: ''
  })
  
  // 密钥展示弹窗（一次性secret）
  const secretShowDialog = ref(false)
  const newSecret = reactive({
    accessKey: '',
    secretKey: ''
  })
  
  // 复制文本
  const copyText = async (text) => {
    await navigator.clipboard.writeText(text)
    ElMessage.success('复制成功')
  }
  
  // 打开创建弹窗
  const openCreateDialog = () => {
    createForm.secretName = ''
    createDialogVisible.value = true
  }
  
  // 创建密钥
  const createSecret = async () => {
    if (!createForm.secretName.trim()) {
      ElMessage.warning('请填写密钥备注名称')
      return
    }
    createDialogVisible.value = false
  
    // 模拟接口创建密钥
    // const res = await api.createSecret(createForm)
    // if(res.code === 200){
    newSecret.accessKey = 'AK_8f2e91367d4041a9b7c2136fa328df11'
    newSecret.secretKey = 'SK_77a92fc3451e433190ac62bc38621199'
    secretShowDialog.value = true
    getList()
    // }
  }
  
  // 启用/禁用
  const toggleStatus = async (row) => {
    const text = row.status === 'enable' ? '禁用' : '启用'
    ElMessageBox.confirm(`确定要${text}该密钥吗？`, '提示', {
      confirmButtonText: '确认',
      cancelButtonText: '取消'
    }).then(async () => {
      // await api.updateSecretStatus({ id: row.id, status: row.status === 'enable' ? 'disable' : 'enable' })
      ElMessage.success(`${text}成功`)
      getList()
    })
  }
  
  // 删除密钥
  const deleteSecret = async (row) => {
    ElMessageBox.confirm('删除后密钥立即失效，无法恢复，确认删除？', '危险操作', {
      type: 'error',
      confirmButtonText: '确认删除',
      cancelButtonText: '取消'
    }).then(async () => {
      // await api.deleteSecret({ id: row.id })
      ElMessage.success('删除成功')
      getList()
    })
  }
  
  // 查询
  const handleSearch = () => {
    page.pageNum = 1
    getList()
  }
  
  // 重置
  const resetQuery = () => {
    query.keyword = ''
    query.status = ''
    handleSearch()
  }
  
  // 获取列表
  const getList = async () => {
    const params = {
      keyword: query.keyword || undefined,
      status: query.status || undefined,
      pageNum: page.pageNum,
      pageSize: page.pageSize
    }
    console.log('密钥查询参数', params)
  
    // 模拟数据
    tableList.value = [
      {
        id: 1,
        secretName: '生产业务服务',
        accessKey: 'AK_8f2e91367d4041a9b7c2136fa328df11',
        status: 'enable',
        createTime: '2026-07-01 10:22:11',
        lastCallTime: '2026-07-25 14:10:33'
      },
      {
        id: 2,
        secretName: '测试环境调试',
        accessKey: 'AK_32ee67821c444732911234719ac22155',
        status: 'disable',
        createTime: '2026-06-15 09:10:22',
        lastCallTime: '2026-06-28 16:42:10'
      }
    ]
    page.total = tableList.value.length
  }
  
  onMounted(() => {
    getList()
  })
  </script>
  
  <style lang="scss" scoped>
  .secret-wrap {
    width: 100%;
    min-height: 100vh;
    padding: 24px;
    font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif;
    background:
      radial-gradient(1200px 600px at 10% -10%, #e8efff 0%, transparent 60%),
      radial-gradient(900px 500px at 110% 110%, #f0eaff 0%, transparent 55%),
      linear-gradient(180deg, #f7f9ff 0%, #eef2fb 100%);
  
    .container {
      max-width: 1400px;
      margin: 0 auto;
      display: flex;
      flex-direction: column;
      gap: 20px;
    }
  
    .quick-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #ffffff;
      border-radius: 16px;
      padding: 18px 24px;
      box-shadow:
        0 1px 2px rgba(16, 24, 40, 0.04),
        0 12px 32px -12px rgba(16, 24, 40, 0.12),
        0 32px 64px -32px rgba(43, 91, 255, 0.18);
      .bar-title {
        font-size: 20px;
        font-weight: 600;
        color: #111827;
      }
      .btn-group {
        display: flex;
        gap: 12px;
      }
    }
  
    .warn-card {
      background: #fff7e8;
      border-radius: 12px;
      padding: 14px 18px;
      display: flex;
      align-items: flex-start;
      gap: 10px;
      .warn-dot {
        width: 8px;
        height: 8px;
        margin-top: 6px;
        border-radius: 50%;
        background: #f59e0b;
        flex-shrink: 0;
      }
      .warn-text {
        font-size: 13px;
        color: #92400e;
        line-height: 1.6;
      }
    }
  
    .filter-card {
      background: #ffffff;
      border-radius: 16px;
      padding: 16px 20px;
      box-shadow:
        0 1px 2px rgba(16, 24, 40, 0.04),
        0 12px 32px -12px rgba(16, 24, 40, 0.12),
        0 32px 64px -32px rgba(43, 91, 255, 0.18);
    }
  
    .table-wrap {
      flex: 1;
      background: #fff;
      border-radius: 16px;
      padding: 16px;
      box-shadow:
        0 1px 2px rgba(16, 24, 40, 0.04),
        0 12px 32px -12px rgba(16, 24, 40, 0.12),
        0 32px 64px -32px rgba(43, 91, 255, 0.18);
      .ak-row {
        display: flex;
        align-items: center;
        gap: 8px;
        .ak-text {
          font-family: Consolas, monospace;
          font-size: 13px;
        }
      }
    }
  
    .page-bar {
      width: 100%;
      height: 48px;
      display: flex;
      padding: 10px 0;
      justify-content: flex-end;
      align-items: center;
    }
  
    // 创建成功弹窗密钥展示
    .secret-show-box {
      padding: 10px 0;
      .show-row {
        display: flex;
        flex-direction: column;
        margin-bottom: 16px;
        label {
          font-size: 14px;
          color: #6b7280;
          margin-bottom: 6px;
        }
        .copy-line {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 12px;
          background: #f8fafc;
          border-radius: 8px;
          font-family: Consolas;
          font-size: 13px;
          word-break: break-all;
        }
      }
    }
    .danger-tip {
      margin-top: 12px;
      padding: 10px;
      background: #fef2f2;
      border-radius: 8px;
      color: #dc2626;
      font-size: 13px;
    }
  }
  
  @media (max-width:720px){
    .secret-wrap{
      padding:12px;
    }
    .quick-bar{
      flex-direction: column;
      align-items: flex-start;
      gap:12px;
    }
  }
  </style>
  